import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import AWS from 'aws-sdk';
import axios from 'axios';
import BoundingBoxOverlay from './BoundingBoxOverlay';
import AccellorLoading from './AccellorLoading';
import config from '../config';

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFContainer = styled.div`
  width: 50%;
  height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PageContainer = styled.div`
  position: relative;
  margin: 20px auto;
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  max-width: 100%;
`;

const PageWrapper = styled.div`
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  -ms-overflow-style: auto;
  
  /* Webkit scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
    background-color: #f5f5f5;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f5f5f5;
  }
`;

const LoadingMessage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  z-index: 100;
`;

const LoadingText = styled.div`
  text-align: center;
  margin-top: 20px;
  color: #666;
  font-size: 15px;
  line-height: 1.5;

  .main-message {
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 6px;
  }

  .sub-message {
    font-size: 14px;
    color: #7f8c8d;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #cc0000;
`;

const PDFViewer = ({ file, selectedBox }) => {
  const containerRef = useRef();
  const [numPages, setNumPages] = useState(null);
  const [pageWidth, setPageWidth] = useState(800);
  const [currentPageDimensions, setCurrentPageDimensions] = useState({});
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ main: 'Initializing PDF viewer...', sub: 'Getting things ready' });

  const loadingMessages = [
    { main: 'Almost there...', sub: 'Preparing your document for viewing' },
    { main: 'Just a moment...', sub: 'Processing the pages' },
    { main: 'Hang tight!', sub: 'Loading the document contents' },
    { main: 'Still working...', sub: 'This PDF is quite large' },
    { main: 'Thanks for your patience', sub: 'The document is being optimized' },
    { main: 'Nearly finished...', sub: 'Final touches being applied' }
  ];

  useEffect(() => {
    if (isLoading) {
      let messageIndex = 0;
      const messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % loadingMessages.length;
        setLoadingProgress(loadingMessages[messageIndex]);
      }, 3000);

      return () => clearInterval(messageInterval);
    }
  }, [isLoading]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoadingProgress({
      main: `Processing ${numPages} pages...`,
      sub: numPages > 10 ? 'This might take a little longer than usual' : 'Should be ready soon'
    });
    
    // Simulate extended loading for large PDFs
    const loadingTime = Math.min(numPages * 100, 5000); // Cap at 5 seconds
    setTimeout(() => {
      setIsLoading(false);
    }, loadingTime);
  };

  const onPageLoadSuccess = (page, pageNumber) => {
    const viewport = page.getViewport({ scale: 1 });
    const containerWidth = containerRef.current?.clientWidth || 800;
    
    setCurrentPageDimensions(prev => ({
      ...prev,
      [pageNumber]: {
        width: viewport.width,
        height: viewport.height,
        scale: viewport.scale
      }
    }));
    
    setPageWidth(containerWidth);
    setLoadingProgress({
      main: `Loading page ${pageNumber}...`,
      sub: pageNumber === numPages ? 'Almost finished!' : 'Making good progress'
    });
  };

  useEffect(() => {
    if (selectedBox && selectedBox.bbox) {
      const pageElement = containerRef.current?.querySelector(
        `[data-page-number="${selectedBox.pageNumber + 1}"]`
      );
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedBox]);

  useEffect(() => {
    const handleResize = () => {
      setPageWidth(containerRef.current?.clientWidth || 800);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!file) {
    return <PDFContainer>Please upload a PDF file</PDFContainer>;
  }

  return (
    <PDFContainer ref={containerRef}>
      <PageWrapper>
        <Document 
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <LoadingMessage>
              <LoadingWrapper>
                <AccellorLoading size={100} />
                <LoadingText>
                  <div className="main-message">{loadingProgress.main}</div>
                  <div className="sub-message">{loadingProgress.sub}</div>
                </LoadingText>
              </LoadingWrapper>
            </LoadingMessage>
          }
          error={<ErrorMessage>Error loading PDF. Please try again.</ErrorMessage>}
        >
          {isLoading ? null : Array.from(new Array(numPages), (el, index) => (
            <PageContainer key={`page_${index + 1}`} data-page-number={index + 1}>
              <Page
                pageNumber={index + 1}
                width={pageWidth}
                onLoadSuccess={(page) => onPageLoadSuccess(page, index + 1)}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
              {selectedBox && 
               selectedBox.pageNumber === index && 
               selectedBox.bbox && 
               currentPageDimensions[index + 1] && (
                <BoundingBoxOverlay
                  bbox={selectedBox.bbox}
                  containerWidth={pageWidth}
                  pdfDimensions={currentPageDimensions[index + 1]}
                  label={selectedBox.label}
                />
              )}
            </PageContainer>
          ))}
        </Document>
        {isLoading && (
          <LoadingMessage>
            <LoadingWrapper>
              <AccellorLoading size={100} />
              <LoadingText>
                <div className="main-message">{loadingProgress.main}</div>
                <div className="sub-message">{loadingProgress.sub}</div>
              </LoadingText>
            </LoadingWrapper>
          </LoadingMessage>
        )}
      </PageWrapper>
    </PDFContainer>
  );
};

export default PDFViewer; 